// src/components/auth/RegistrationWizard.jsx
import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Header from "@/components/tables/dashboard/Header";
import mesh from "@assets/images/Mesh.png";
import register1 from "@assets/images/register1.png";
import register2 from "@assets/images/register2.png";
import register3 from "@assets/images/register3.png";
import { toast } from "react-toastify";
import JSEncrypt from "jsencrypt";
import { BASE_URL } from "@utils/constants";
import { useNavigate } from "react-router-dom";
import Step1_AccountPersonal from "./Step1Account";
import Step2_LocationInstitution from "./Step2Details";
import Step3_LanguagesCaptcha from "./Step3Languages";
import { ArrowRight } from "lucide-react";
import { useDispatch } from "react-redux";

const steps = [
  { number: 1, title: "Enter your email address" },
  { number: 2, title: "Provide your basic info" },
  { number: 3, title: "Create your password" },
];

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};


const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const mobilRegex = "^[6-9]\\d{9}$";

const validationSchemas = Yup.object({
  username: Yup.string().required("Required"),
  email: Yup.string()
    .required("Email is required")
    .matches(
      emailRegex,
      "Enter a valid email address (e.g. name@example.com)"
    ),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Required"),
  password2: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Required"),

  firstName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "First name must not contain numbers or special characters")
    .required("Required"),

  enrollmentNo: Yup.string().required("Required"),

  lastName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Last name must not contain numbers or special characters")
    .required("Required"),

  mobileNo: Yup.string()
    .matches(mobilRegex, "Mobile number must be 10 digits")
    .required("Required"),
  gender: Yup.string().required("Required"),
  state: Yup.number().required("Required"),
  city: Yup.number().required("Required"),
  organization: Yup.number().required("Required"),
  college: Yup.number().required("Required"),
  targetLanguages: Yup.array()
    .min(1, "Please select at least one language")
    .required("Required"),
  captchaInput: Yup.string().required("CAPTCHA is required"),
});

const Registration = () => {
  const OTP_TIME = 10 * 60;
  const [timeLeft, setTimeLeft] = useState(OTP_TIME);
  const hasTimerStarted = useRef(false);
  const timerRef = useRef(null);
  const [step, setStep] = useState(1);
  const [token, setToken] = useState("");
  const [publicKey, setPublicKey] = useState(null);
  const [captchaData, setCaptchaData] = useState({ captchaImage: null, captchaToken: "" });
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [resentCaptcha, setResentCaptcha] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);
  const [otpError, setOtpError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchPublicKey = async () => {
    const res = await fetch(`${BASE_URL}api/user/public-key`);
    const data = await res.json();
    if (data.data) setPublicKey(data.data);
  };

  const fetchCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaError(null);
    try {
      const response = await fetch(`${BASE_URL}api/user/captcha`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.captchaImage && data.captchaToken) {
        setCaptchaData({ captchaImage: data.captchaImage, captchaToken: data.captchaToken });
      } else {
        setCaptchaError("Failed to load CAPTCHA. Please try again.");
      }
    } catch (err) {
      console.error("[fetchCaptcha] Error:", err);
      setCaptchaError("Failed to load CAPTCHA. Please try again.");
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicKey();
    fetchCaptcha();
  }, []);

  useEffect(() => {
    if ((step === 2 || step === 3) && !hasTimerStarted.current) {
      hasTimerStarted.current = true;

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }

  }, [step]);

  useEffect(() => {
    if (timeLeft <= 0) {
      clearInterval(timerRef.current);
      hasTimerStarted.current = false;

      toast.error("Session expired. Please verify OTP again.");

      setStep(1);
      setToken("");
      setTimeLeft(OTP_TIME);
    }
  }, [timeLeft]);




  const goToLogin = () => {
    navigate("/")
  }


  const handleSubmit = async (values) => {
    if (step < 3) {
      setStep(step + 1);
      return;
    }

    try {
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedPassword = encrypt.encrypt(values.password);

      const payload = {
        username: values.username,
        email: values.email,
        password: encryptedPassword,
        firstName: values.firstName,
        middleName: values.middleName || "",
        lastName: values.lastName,
        gender: values.gender,
        mobileNumber: parseInt(values.mobileNo),
        enrollmentNumber: values.enrollmentNo,
        stateId: values.state,
        cityId: values.city,
        organizationId: values.organization,
        collegeId: values.college,
        knowLangIds: values.targetLanguages.map(id => parseInt(id)),
        captchaText: values.captchaInput,
        captchaToken: captchaData.captchaToken,
        finalOtp: values.otp.join("")

      };

      const res = await fetch(`${BASE_URL}api/user/register`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (result.success) {
        clearInterval(timerRef.current);
        hasTimerStarted.current = false;
        toast.success("Registration successful! Please log in.");
        navigate("/");
      } else {
        toast.error(result.message || "Registration failed");
        setResentCaptcha(true);
      }
    } catch (err) {
      console.error("Registration error:", err);
      toast.error("Something went wrong");
    }
  };

  const verifyOTP = async (values) => {
    const mobile = values.mobileNo;
    const email = values.email;

    if (!mobile || !email) {
      toast.error("Enter mobile & email first");
      return;
    }
    try {
      const res = await fetch(BASE_URL + "api/user/register/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: values.otp.join("") }),
      });

      const data = await res.json();
      return data;
    } catch (err) {
      console.error("OTP error:", err);
    } finally {
    }
  };

  return (
    <>
      <Header />
      <div
        className="min-h-[calc(100vh-97px)] bg-cover bg-center flex items-center justify-center p-4"
        style={{ backgroundImage: `url(${mesh})` }}
      >
        <div className="max-w-6xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row">
          {/* Left: Form */}
          <div className={`${step == 2 || step == 3 ? 'lg:w-3/5' : 'lg:w-2/4'} p-2 flex items-center justify-center`}>
            <div className="w-full bg-white shadow-lg p-10 m-4 shadow-lg gradient-top-border rounded-2xl">

              <h1 className="text-3xl font-bold mb-2">Create an account</h1>
              <p className="mb-4">Already have an account? <span className="text-[rgba(236,112,38,1)] underline hover:cursor-pointer" onClick={goToLogin}>Log In</span></p>

              {/* Progress Bar */}
              <div className="flex items-center mb-3 w-full">
                {steps.map((s, i) => (
                  <div key={s.number} className="flex flex-1">
                    {/* Step Circle and Label */}
                    <div className="flex flex-col items-center w-20">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all 
                          ${step === s.number
                            ? "bg-[#ec7026] text-white"           /* current */
                            : step > s.number
                              ? "bg-green-600 text-white"            /* completed */
                              : "bg-gray-300 text-gray-600"          /* upcoming */
                          }`}
                      >
                        {s.number}
                      </div>

                      <span
                        className={`mt-2 text-center text-[12px] w-full 
                             ${step === s.number
                            ? "text-[#ec7026]"
                            : step > s.number
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                      >
                        {s.title}
                      </span>

                    </div>

                    {/* Connector Line */}
                    {i < steps.length - 1 && (
                      <div
                        className={`h-1 my-auto flex-1 mx-2 transition-all 
                      ${step > s.number ? "bg-green-600" : "bg-gray-300"}`}
                      ></div>

                    )}
                  </div>

                ))}
              </div>

              <Formik
                initialValues={{
                  username: "", email: "", password: "", password2: "",
                  firstName: "", middleName: "", lastName: "", mobileNo: "", gender: "",
                  state: "", city: "", organization: "", college: "", enrollmentNo: "",
                  targetLanguages: [], captchaInput: "", otp: ["", "", "", "", "", ""],
                }}
                validationSchema={validationSchemas}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, errors, touched, values, validateForm, setTouched, setFieldValue }) => {


                  const nextPage = async () => {
                    const errors = await validateForm();
                    if (step === 1) {
                      if (errors.username || errors.email || errors.mobileNo) {
                        setTouched({
                          username: true,
                          email: true,
                          mobileNo: true,
                        });
                        toast.error("Please fill all required fields");
                        return;
                      }
                      const res = await verifyOTP(values);
                      if (res.success) {
                        setStep((prev) => prev + 1);
                      } else {
                        toast.error(res.message || "OTP verification failed. Please check your OTP and try again.");
                      }
                    }

                    if (step === 2) {
                      if (errors.state || errors.city || errors.organization || errors.college || errors.firstName || errors.lastName) {
                        setTouched({
                          state: true,
                          city: true,
                          organization: true,
                          college: true,
                          firstName: true,
                          lastName: true,
                        });
                        toast.error("Please fill all required fields");
                        return;
                      }
                      setStep((prev) => prev + 1);
                    }
                  };



                  return (
                    <>
                      {step !== 1 && (
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-4">
                          ⏳
                          <span>Time remaining:</span>
                          <span className="px-3 py-1 rounded-full bg-red-100 text-red-600 font-bold tracking-wide">
                            {formatTime(timeLeft)}
                          </span>
                        </h2>

                      )}

                      <Form id="formik-form">
                        {step === 1 && <Step1_AccountPersonal touched={touched} errors={errors} values={values} setFieldValue={setFieldValue} />}
                        {step === 2 && <Step2_LocationInstitution values={values} setFieldValue={setFieldValue} touched={touched} errors={errors} />}
                        {step === 3 && <Step3_LanguagesCaptcha resentCaptcha={resentCaptcha} token={token} touched={touched} errors={errors} values={values} setFieldValue={setFieldValue} fetchCaptcha={fetchCaptcha} captchaData={captchaData} captchaLoading={captchaLoading} captchaError={captchaError} />}
                      </Form>
                      <div className="flex justify-between mt-1">
                        {step > 1 && (
                          <button
                            type="button"
                            disabled={step === 2}
                            onClick={() => setStep((s) => s - 1)}
                            className="px-6 py-3 bg-gray-400 text-white rounded-lg"
                          >
                            Back
                          </button>
                        )}

                        {step < 3 && (
                          <button
                            type="button"
                            onClick={nextPage}
                            className="px-5 py-3 bg-[#ec7026] text-white rounded-lg ml-auto flex items-center"
                          >
                            Next <ArrowRight className="h-4 w-4" />
                          </button>
                        )}

                        {step === 3 && (
                          <button
                            type="submit"
                            form="formik-form"
                            disabled={isSubmitting}
                            className="px-5 py-3 bg-[#ec7026] text-white rounded-lg ml-auto"
                          >
                            {isSubmitting ? "Submitting..." : "Complete Registration"}
                          </button>
                        )}
                      </div>
                    </>
                  )
                }}
              </Formik>
            </div>
          </div>

          {/* Right: Image */}
          <div className="hidden lg:flex lg:w-2/4 items-center justify-center p-12">
            <img src={step === 1 ? register1 : step === 2 ? register2 : register3} alt="Registration" className="max-w-full" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Registration;